/**
 * DocumentThumbnail — 40×40px status-colored icon container.
 * Shows a document line pattern, status-specific background color,
 * animated spinner for processing states, and dimming overlay for exported docs.
 */
import { File06, Loading01 } from "@untitledui/icons";
import type { HomeDocumentStatus } from "@/types/home";
import { cx } from "@/utils/cx";

interface DocumentThumbnailProps {
    status: HomeDocumentStatus;
    className?: string;
}

const BG_CLASS_MAP: Record<HomeDocumentStatus, string> = {
    error: "bg-error-primary",
    alert: "bg-error-primary",
    exported: "bg-secondary",
    processing: "bg-brand-primary",
    reading: "bg-brand-primary",
    detecting_type: "bg-brand-primary",
    finding_vendor: "bg-brand-primary",
    finding_receipts: "bg-brand-primary",
    uploading: "bg-brand-primary",
    matching: "bg-brand-primary",
    mismatch: "bg-[#fff1f3]",
    duplicate: "bg-[#f4f3ff]",
    review_required: "bg-warning-primary",
    ready: "bg-primary",
    queued: "bg-primary",
};

const ANIMATING_STATUSES: HomeDocumentStatus[] = ["processing", "reading", "detecting_type", "finding_vendor", "finding_receipts", "uploading", "queued"];

export function DocumentThumbnail({ status, className }: DocumentThumbnailProps) {
    const isAnimating = ANIMATING_STATUSES.includes(status);
    const isExported = status === "exported";
    const bgClass = BG_CLASS_MAP[status] ?? "bg-primary";

    return (
        <div
            className={cx(
                "relative flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-secondary shadow-xs",
                bgClass,
                className,
            )}
        >
            {/* Document line pattern */}
            <div className="pointer-events-none absolute inset-0 flex flex-col gap-1 p-1.5 opacity-[0.08]">
                <div className="h-px w-full rounded-full bg-fg-primary" />
                <div className="h-px w-3/4 rounded-full bg-fg-primary" />
                <div className="h-px w-1/2 rounded-full bg-fg-primary" />
            </div>

            {/* Status icon */}
            {isAnimating ? (
                <span className="relative z-10 inline-flex size-4 animate-spin items-center justify-center [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-fg-brand-primary">
                    <Loading01 />
                </span>
            ) : (
                <File06 className={cx("relative z-10 size-4", isExported ? "text-fg-disabled" : "text-fg-quaternary")} />
            )}

            {/* Exported dimming overlay */}
            {isExported && <div className="absolute inset-0 bg-primary/40" />}
        </div>
    );
}
